import type { CSSProperties, RefObject, ReactElement, Ref } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Typography } from '@web-chat-core/ui-kit';
import { TooltipContent } from './view';

interface Props {
    label: React.ReactNode;
    show?: boolean;
    ariaLabel?: string;
    children: ReactElement;
    style?: CSSProperties;
    size?: 'small' | 'medium';
    maxWidth?: string;
    alwaysAtRight?: boolean;
    oneRow?: boolean;
    alwaysTop?: boolean;
    hideBeak?: boolean;
    position?: 'top' | 'bottom' | 'left' | 'right';
    whiteSpace?: 'normal' | 'pre' | 'pre-wrap' | 'pre-line';
    disableHoverableContent?: boolean;
    manuallyOpened?: boolean;
    onChange?: (isOpen: boolean) => void;
    cy?: string;
    background?: string;
    delay?: number;
    variant?: 'default' | 'info';
    textAlign?: 'left' | 'center' | 'right' | 'start' | 'end';
    sideOffset?: number;
    // Внешний ref на DOM-узел пузыря (портализованный контент). Мержится с внутренним tooltipRef.
    // Callback-ref сработает ровно в момент маунта/анмаунта пузыря — удобно для замеров его геометрии.
    contentRef?: Ref<HTMLDivElement>;
}

const ONE_ROW_MAX_HEIGHT = 35;

const hasHover = window.matchMedia?.('(hover: hover)').matches ?? false;

export const Tooltip = ({ children, ...props }: Props) =>
    hasHover ? <HoverTooltip {...props}>{children}</HoverTooltip> : children;

const HoverTooltip = (props: Props) => {
    const {
        children,
        show = true,
        label,
        ariaLabel,
        maxWidth,
        alwaysAtRight,
        hideBeak,
        alwaysTop,
        position,
        whiteSpace = 'normal',
        oneRow = true,
        size = 'medium',
        disableHoverableContent = false,
        manuallyOpened,
        onChange,
        cy,
        background,
        delay = 0,
        variant = 'default',
        textAlign = 'center',
        sideOffset,
        contentRef,
    } = props;

    const tooltipRef = useRef<HTMLDivElement | null>(null);

    // Пишем узел в оба ref-а: внутренний (нужен для расчёта oneRow)
    // и внешний из пропсов (удобен для замеров геометрии).
    const setContentRef = useCallback(
        (node: HTMLDivElement | null) => {
            tooltipRef.current = node;
            if (typeof contentRef === 'function') {
                contentRef(node);
            } else if (contentRef) {
                (contentRef as RefObject<HTMLDivElement | null>).current = node;
            }
        },
        [contentRef],
    );
    const [isOneRow, setIsOneRow] = useState(oneRow);

    const [open, setOpen] = useState(false);

    const [radixOpen, setRadixOpen] = useState(false);

    // Определяем сторону и выравнивание на основе пропсов
    const getSide = (): 'top' | 'right' | 'bottom' | 'left' => {
        if (position) return position;
        if (alwaysTop) return 'top';
        if (alwaysAtRight) return 'right';

        return 'top';
    };

    useEffect(() => {
        if (!show || !open || !tooltipRef.current || !oneRow) return;

        const tooltipPopup = tooltipRef.current.getBoundingClientRect();

        // определяем что много строк, тогда меняем шрифт и добавляем треугольник-птичку
        if (tooltipPopup.height && tooltipPopup.height > ONE_ROW_MAX_HEIGHT) {
            setIsOneRow(false);
        } else {
            setIsOneRow(true);
        }

        return () => {
            tooltipRef.current = null;
        };
    }, [oneRow, open, show]);

    const onOpenChange = useCallback(
        (openState: boolean) => {
            onChange?.(openState);
            setRadixOpen(openState);
        },
        [onChange],
    );

    useEffect(() => {
        if (!show) {
            setOpen(false);

            return;
        }
        if (typeof manuallyOpened === 'boolean') {
            // force to open or close if value changes
            setOpen(manuallyOpened);
        } else {
            setOpen(radixOpen);
        }
    }, [radixOpen, manuallyOpened, show]);

    return (
        <TooltipPrimitive.Provider
            delayDuration={delay}
            disableHoverableContent={disableHoverableContent}
        >
            <TooltipPrimitive.Root
                open={open}
                onOpenChange={onOpenChange}
            >
                <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
                {open && (
                    <TooltipPrimitive.Portal>
                        <TooltipContent
                            ref={setContentRef}
                            side={getSide()}
                            size={size}
                            align="center"
                            sideOffset={sideOffset}
                            aria-label={ariaLabel}
                            $maxWidth={maxWidth}
                            $isOneRow={isOneRow}
                            $hideBeak={hideBeak}
                            $whiteSpace={whiteSpace}
                            $variant={variant}
                            $textAlign={textAlign}
                            collisionPadding={10}
                            data-cy={cy}
                            background={background}
                        >
                            {variant === 'info' ? <Typography variant="body1R">{label}</Typography> : label}
                        </TooltipContent>
                    </TooltipPrimitive.Portal>
                )}
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
};
